import { useContext, useMemo } from 'react'

import { DateTime } from 'luxon'

import { digObject, launchModal, sortArrayBy } from '@domain-group/javascript-utils'

import { useThunkDispatch, useWatchEntityUpdates } from '@domain-group/react-hooks'

import useCampaignContributions from '@hooks/useCampaignContributions'
import { generateUrls } from '@hooks/useInvoice'
import useSelector from '@hooks/useSelector'

import useAccountingItems from
  '@sections/Client/packs/Accounting/screens/Invoices/screens/List/hooks/useAccountingItems'

import type {
  CampaignContributionModel, CreditNoteModel, InvoiceModel,
} from '@models/types'

import { getPaidByTitleByKey, getPaymentMethodByKey } from '@functions/campaignContribution'
import { formatEntityCount } from '@functions/entity'
import { getInvoiceStatus } from '@functions/invoice'

import type { EntitiesState } from '@redux/entities'

import PageContext from '@contexts/pageContext'

import { AppDispatch } from '@redux/store'
import { deleteCreditNote } from '@redux/modules/creditNote'
import { deleteCampaignContribution } from '@redux/modules/campaignContribution'

const watchEntityKeys = [
  'campaignContributions',
  'campaignItems',
  'campaignQuotes',
  'creditNotes',
  'invoices',
  'orders',
]

export interface Item {
  amount: number,
  amountType: string,
  authenticatedDownloadUrl: string,
  campaign_id?: number,
  date: string,
  deletable: boolean,
  downloadUrl: string,
  editable: boolean,
  entityType: string,
  id: number,
  itemType: string,
  sort: number,
  status: string,
  tags: string[],
  title: string,
  tooltip?: string,
}

const mapInvoices = (invoices: InvoiceModel[]) => invoices.map((invoice) => {
  const { campaign_id, dates } = invoice
  const approvalDateString = digObject(dates, 'approval.date_time')
  const dueDateString = digObject(dates, 'due.date')

  const parsedDate = DateTime.fromFormat(approvalDateString, 'dd/MM/yyyy HH:mm')

  const approvalDateFormatted = parsedDate.toFormat('dd MMM yyyy')
  const sortTimeStamp = parsedDate.valueOf()

  const debtorTitle = digObject(invoice, 'debtor.title', 'Unknown')

  const { pdfDownloadUrl } = generateUrls(invoice)

  return {
    amount: invoice.total,
    amountType: invoice.total >= 0 ? 'debit' : 'credit',
    campaign_id,
    date: approvalDateFormatted,
    deletable: false,
    authenticatedDownloadUrl: pdfDownloadUrl,
    downloadUrl: '',
    editable: false,
    entityType: 'Invoice',
    id: invoice.id,
    itemType: 'invoice',
    sort: sortTimeStamp,
    status: getInvoiceStatus(invoice),
    tags: [`Due ${dueDateString}`],
    title: `Invoice #${invoice.id}`,
    tooltip: `Debtor: ${debtorTitle}`,
  }
})

const mapCreditNotes = (creditNotes: CreditNoteModel[]) => creditNotes.map((creditNote) => {
  const {
    // @ts-expect-error TS2339
    campaign_id, data, dates, id, items, reference, total,
  } = creditNote

  const dateString = digObject(dates, 'created.date')

  const itemCount = items ? items.length : 0

  const parsedDate = DateTime.fromFormat(dateString, 'dd/MM/yyyy')
  const sortTimeStamp = parsedDate.valueOf()

  const dateStringFormatted = parsedDate.toFormat('dd MMM yyyy')

  return {
    amount: total,
    amountType: total >= 0 ? 'credit' : 'debit',
    campaign_id,
    date: dateStringFormatted,
    deletable: !(data && data.xero_id),
    authenticatedDownloadUrl: '',
    downloadUrl: '',
    editable: true,
    entityType: 'CreditNote',
    id,
    itemType: 'credit',
    sort: sortTimeStamp,
    status: 'Received',
    tags: [formatEntityCount({ entityString: 'Item', entityCount: itemCount })],
    title: reference,
  }
})

const mapBills = (
  invoices: InvoiceModel[],
  invoiceAttachments: EntitiesState['invoiceAttachments'],
) => invoices.map((invoice) => {
  const {
    attachments, campaign_id, created_by, creditor_name, dates, id, links, reference, total,
  } = invoice

  const createdDateString = digObject(dates, 'created.date_time')
  const dueDateString = digObject(dates, 'due.date')

  const parsedDate = DateTime.fromFormat(createdDateString, 'dd/MM/yyyy HH:mm')
  const sortTimeStamp = parsedDate.valueOf()

  const createdDateStringFormatted = parsedDate.toFormat('dd MMM yyyy')

  // Download Links
  let authenticatedDownloadUrl = ''
  let downloadUrl = ''

  if (created_by === 'debtor' && attachments){
    // @ts-expect-error TS2538
    const attachment = invoiceAttachments[attachments[0]] || {}
    downloadUrl = digObject(attachment, 'links.file_url', '')
  }

  if (created_by !== 'debtor'){
    const { pdfDownloadUrl } = generateUrls(invoice)
    authenticatedDownloadUrl = pdfDownloadUrl
  }

  return {
    amount: total,
    amountType: total >= 0 ? 'debit' : 'credit',
    campaign_id,
    date: createdDateStringFormatted,
    deletable: created_by === 'debtor',
    authenticatedDownloadUrl,
    downloadUrl,
    editable: false,
    entityType: 'Invoice',
    id,
    itemType: 'bill',
    sort: sortTimeStamp,
    status: getInvoiceStatus(invoice),
    tags: [`Due ${dueDateString}`],
    title: created_by === 'debtor' ? reference : `Invoice #${id}`,
    tooltip: `Creditor: ${creditor_name || 'Unknown'}`,
  }
})

const mapPayments = (contributions: CampaignContributionModel[]) => contributions.map((contribution) => {
  const {
    amount, campaign_id, dates, id, paid_by, payment_method, payment_received, reference,
  } = contribution

  const dateString = digObject(dates, 'created.date_time', '')

  const parsedDate = DateTime.fromFormat(dateString, 'dd/MM/yyyy HH:mm')
  const sortTimeStamp = parsedDate.valueOf()
  const dueDateStringFormatted = parsedDate.toFormat('dd MMM yyyy')

  return {
    amount,
    // @ts-expect-error TS18048
    amountType: amount >= 0 ? 'credit' : 'debit',
    campaign_id,
    date: dueDateStringFormatted,
    deletable: !payment_received,
    authenticatedDownloadUrl: '',
    downloadUrl: digObject(contribution, 'links.receipt_url', ''),
    editable: false,
    entityType: 'CampaignContribution',
    id,
    itemType: 'payment',
    sort: sortTimeStamp,
    status: payment_received ? 'Received' : 'Not Received',
    // @ts-expect-error TS2345
    tags: [`${getPaidByTitleByKey(paid_by)} via ${getPaymentMethodByKey(payment_method)}`],
    title: reference,
  }
})

interface MapMergeItemsParams {
  approvedInvoices: InvoiceModel[],
  debtorInvoices: InvoiceModel[],
  creditorAccountItems: (InvoiceModel | CreditNoteModel)[],
  contributions: CampaignContributionModel[],
  invoiceAttachments: EntitiesState['invoiceAttachments'],
}

const mapMergeItems = (params: MapMergeItemsParams) => {
  const {
    approvedInvoices, debtorInvoices, creditorAccountItems, contributions, invoiceAttachments,
  } = params

  const items = [
    ...mapBills(debtorInvoices, invoiceAttachments),
    ...mapCreditNotes(creditorAccountItems.filter(item => item.type === 'CreditNote')),
    ...mapInvoices(approvedInvoices),
    ...mapPayments(contributions),
  ]

  return sortArrayBy(items, 'asc', 'sort')
}

const getPendingInvoices = (
  items: (InvoiceModel | CreditNoteModel)[],
) => {
  const pendingInvoices = items.filter(
    // @ts-expect-error TS2339
    item => !item.approved && item.type !== 'CreditNote',
  ) as InvoiceModel[]

  // @ts-expect-error TS2345
  sortArrayBy(pendingInvoices, 'asc', invoice => DateTime.fromFormat(invoice.dates.due.date, 'dd/MM/yyyy'))

  return pendingInvoices
}

const getApprovedInvoices = (
  items: (InvoiceModel | CreditNoteModel)[],
// @ts-expect-error TS2339
) => items.filter(item => item.approved && item.type !== 'CreditNote') as InvoiceModel[]

type DeleteItemParams = {
  dispatch: AppDispatch,
  item: Item,
}

const deleteItem = (params: DeleteItemParams) => {
  const { dispatch, item } = params
  const { entityType } = item

  if (entityType === 'CreditNote'){
    return dispatch(deleteCreditNote(item))
  }

  if (entityType === 'CampaignContribution'){
    return dispatch(deleteCampaignContribution(item))
  }

  return new Promise(resolve => resolve({ success: false }))
}

type EditItemParams = {
  callbacks: {[key: string]: (payload: { [key: string]: any } | undefined) => void},
  item: Item,
}

const editItem = (params: EditItemParams) => {
  const { callbacks, item } = params

  if (item.entityType === 'CreditNote'){
    return launchModal({ callbacks, modalKey: 'CreateOrEditCreditNoteModal', payload: { creditNote: item } })
  }

  return new Promise(resolve => resolve({ success: false }))
}

interface UseCampaignAccountingParams {
  agencyId?: number,
  campaignId: number,
  filters: {
    itemType?: string,
    string?: string,
  },
  performHttpRequests: boolean,
}

const useCampaignAccounting = (params: UseCampaignAccountingParams) => {
  const {
    agencyId, campaignId, filters, performHttpRequests,
  } = params

  const { itemType: filterItemType, string: filterString } = filters || {}
  const {
    updatedEntities: {
      campaignContributions: campaignContributionsUpdatedAt,
      campaignItems: campaignItemsUpdatedAt,
      creditNotes: creditNotesUpdatedAt,
      invoices: invoicesUpdatedAt,
      invoiceAttachments: invoiceAttachmentsUpdatedAt,
    },
  } = useWatchEntityUpdates(watchEntityKeys)

  const { callbacks } = useContext(PageContext)

  // Get all Creditor Items
  const creditorItemsPayload = useAccountingItems({
    filters: {
      campaign_id: campaignId, creditor_type: 'Agency', agency_id: agencyId, string: filterString,
    },
    performHttpRequests,
    requestOptions: {
      include_invoice_attachment_file: true,
      include_credit_note_items: true,
      include_invoice_attachments: true,
      include_invoice_campaign: true,
      include_invoice_creditor: true,
      include_invoice_items: true,
    },
  })

  const { filteredAccountingItems: filteredCreditorAccountItems } = creditorItemsPayload

  const pendingInvoices = getPendingInvoices(filteredCreditorAccountItems)

  const approvedInvoices = getApprovedInvoices(filteredCreditorAccountItems)

  // Get Invoice Attachments to render item count for credit notes
  const { invoiceAttachments } = useSelector(reduxState => reduxState.entities)

  // Get all debtor Items
  const debtorItemsPayload = useAccountingItems({
    filters: {
      approved: 'true', campaign_id: campaignId, debtor_id: agencyId, debtor_type: 'Agency', string: filterString,
    },
    performHttpRequests,
    requestOptions: {
      include_invoice_attachment_file: true,
      include_credit_note_items: true,
      include_invoice_attachments: true,
      include_invoice_campaign: true,
      include_invoice_creditor: true,
    },
  })

  const {
    filteredAccountingItems: filteredDebtorAccountingItems,
    loading: loadingAccountingItems,
  } = debtorItemsPayload

  // Merge debtor and creditor items
  const mergedAccountingItems = filteredCreditorAccountItems.concat(filteredDebtorAccountingItems)

  // Credit Note Total
  const creditNoteTotal = mergedAccountingItems.reduce((acc, item) => {
    if (item.type === 'CreditNote'){
      return acc + item.total
    }
    return acc
  }, 0)

  // Invoice Total
  const invoiceTotal = approvedInvoices.reduce((acc, item) => (item.void ? acc : acc + item.total), 0)

  const {
    contributionsReceived,
    contributionTotal,
    filteredCampaignContributions,
    loading: loadingCampaignContributions,
  } = useCampaignContributions(
    { filters: { campaign_id: campaignId, string: filterString }, performHttpRequests: !!campaignId },
  )

  const sum = creditNoteTotal + contributionsReceived - invoiceTotal
  const calc = sum.toFixed(2)
  const difference = parseFloat(calc)

  // Map into custom object so that we can display in one component on render
  const accountingItems = {
    pendingInvoices,
    items: mapMergeItems({
      approvedInvoices,
      // @ts-expect-error TS2322
      debtorInvoices: filteredDebtorAccountingItems,
      creditorAccountItems: filteredCreditorAccountItems,
      contributions: filteredCampaignContributions,
      invoiceAttachments,
    }),
    totals: {
      contributionTotal,
      contributionsReceived,
      creditNoteTotal,
      difference,
      invoicesApproved: invoiceTotal,
    },
  }

  const { items: statementItems } = accountingItems
  const hasStatementItems = !!statementItems.length

  const loading = loadingAccountingItems || loadingCampaignContributions

  // Filters
  const filteredStatementItems = useMemo(() => {
    const filtered = statementItems.filter(
      (item) => {
        const { itemType } = item
        const matchItemType = filterItemType === undefined
        || filterItemType === itemType
        || (filterItemType === 'campaign_balance' && itemType !== 'bill')

        return matchItemType
      },
    )

    return filtered
  }, [
    JSON.stringify(filters),
    campaignContributionsUpdatedAt,
    campaignItemsUpdatedAt,
    creditNotesUpdatedAt,
    invoicesUpdatedAt,
    invoiceAttachmentsUpdatedAt,
  ])

  const hasFilteredStatementItems = !!filteredStatementItems.length

  const dispatch = useThunkDispatch()

  return {
    callbacks: {
      editItem: (item: Item) => editItem({ callbacks, item }),
      deleteItem: (item: Item) => deleteItem({ dispatch, item }),
    },
    filteredStatementItems,
    hasFilteredStatementItems,
    hasStatementItems,
    loading,
    pendingInvoices,
  }
}

export type UseCampaignAccountingPayload = ReturnType<typeof useCampaignAccounting>

export default useCampaignAccounting
